import React, { useCallback, useState } from 'react';
import BaseModal from '../../components/_BaseModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import { APIError, useAxios } from '../../core/_axios';
import { ActivityReport, ActivityReportAccountRow, ActivityReportMonth } from '../../core/_models';
import { managementGetActivityReport } from '../../core/_requests';

/*
 * Export Report, on the management Accounts list: activity per calendar month for the accounts
 * this user sees, counted by the backend (GET /authenticated/management/activity-report). Shown
 * here, copied as plain text for an email, or downloaded as a CSV with a row per account per month.
 */

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/** "2026-09" → "September 2026" */
export const monthName = (month: string): string => {
    const [year, monthNumber] = month.split('-').map(Number);
    return `${MONTH_NAMES[monthNumber - 1] ?? month} ${year}`;
};

const yearMonthOf = (date: Date): string =>
    `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

const previousYearMonth = (date: Date): string =>
    yearMonthOf(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - 1, 1)));

const num = (value: number | null | undefined): string => (value ?? 0).toLocaleString();

/** "2026-10-23T01:12:33Z" → "2026-10-23 01:12 UTC" */
const generatedLabel = (generatedAt: string): string =>
    generatedAt.replace('T', ' ').replace(/:\d{2}Z$/, ' UTC');

const SUMMARY_COLUMNS: { key: keyof ActivityReportMonth & string; label: string }[] = [
    { key: 'accounts_created', label: 'Accounts created' },
    { key: 'accounts_active', label: 'Accounts active' },
    { key: 'users_active', label: 'Users active' },
    { key: 'ipads_active', label: 'iPads active' },
    { key: 'services_created', label: 'Services created' },
    { key: 'signatures', label: 'Signatures' },
    { key: 'signatures_interested_in_planning', label: 'Interested in planning' },
];

const TOTAL_COLUMNS: { key: keyof ActivityReportMonth & string; label: string }[] = [
    { key: 'total_accounts', label: 'Accounts' },
    { key: 'total_ipads', label: 'iPads' },
    { key: 'total_services', label: 'Services' },
    { key: 'total_signatures', label: 'Signatures' },
];

const ACCOUNT_COLUMNS: { key: keyof ActivityReportAccountRow & string; label: string; numeric?: boolean }[] = [
    { key: 'month', label: 'Month' },
    { key: 'name', label: 'Account' },
    { key: 'agency_name', label: 'Agency' },
    { key: 'created_this_month', label: 'Created this month' },
    { key: 'services_created', label: 'Services created', numeric: true },
    { key: 'signatures', label: 'Signatures', numeric: true },
    { key: 'signatures_interested_in_planning', label: 'Interested in planning', numeric: true },
    { key: 'ipads_active', label: 'iPads active', numeric: true },
    { key: 'services_started_or_stopped', label: 'Services started or stopped', numeric: true },
    { key: 'last_signature_at', label: 'Last signature' },
    { key: 'account_unique_id', label: 'Account id' },
];

const monthLabel = (month: ActivityReportMonth): string =>
    month.to_date ? `${month.month} (to date)` : month.month;

const cellText = (row: ActivityReportAccountRow, key: keyof ActivityReportAccountRow): string => {
    const value = row[key];
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return num(value);
    if (key === 'last_signature_at') return String(value).replace('T', ' ').replace(/Z$/, ' UTC');
    return String(value);
};

/** The summary as plain text, one month per block, ready to paste into an email. */
export function activityReportAsText(report: ActivityReport): string {
    const generated = generatedLabel(report.generated_at);
    const blocks = report.months.map(month => {
        const title = month.to_date
            ? `${monthName(month.month)} (to date, as of ${generated})`
            : monthName(month.month);
        const lines = SUMMARY_COLUMNS.map(column => `- ${column.label}: ${num(month[column.key] as number)}`);
        return [title, ...lines].join('\n');
    });
    return blocks.join('\n\n');
}

const escapeCsv = (value: string): string => `"${value.replace(/"/g, '""')}"`;
const csvLine = (cells: string[]): string => cells.map(escapeCsv).join(',');

/** Both tables as one CSV: the summary, a blank line, then a row per account per month. */
export function activityReportAsCsv(report: ActivityReport): string {
    const lines: string[] = [];
    lines.push(csvLine([`Signature Guestbook activity, ${report.from} to ${report.to}, generated ${generatedLabel(report.generated_at)}`]));
    lines.push('');
    lines.push('Summary');
    lines.push(csvLine(['Month', ...SUMMARY_COLUMNS.map(c => c.label), ...TOTAL_COLUMNS.map(c => `Total ${c.label.toLowerCase()} at month end`)]));
    for (const month of report.months) {
        lines.push(csvLine([
            monthLabel(month),
            ...SUMMARY_COLUMNS.map(c => String(month[c.key] ?? 0)),
            ...TOTAL_COLUMNS.map(c => String(month[c.key] ?? 0)),
        ]));
    }
    lines.push('');
    lines.push('By account');
    lines.push(csvLine(ACCOUNT_COLUMNS.map(c => c.label)));
    for (const row of report.accounts) {
        lines.push(csvLine(ACCOUNT_COLUMNS.map(c => {
            const value = row[c.key];
            if (value === null || value === undefined) return '';
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            return String(value);
        })));
    }
    lines.push('');
    lines.push('What each number means');
    for (const definition of report.definitions) lines.push(csvLine([definition]));
    return lines.join('\n');
}

export default function ExportActivityReportModal({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}) {
    const axios = useAxios();
    const thisMonth = yearMonthOf(new Date());

    const [from, setFrom] = useState<string>(previousYearMonth(new Date()));
    const [to, setTo] = useState<string>(thisMonth);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [report, setReport] = useState<ActivityReport | null>(null);
    const [copied, setCopied] = useState(false);

    const generate = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (loading) return;
        setLoading(true);
        setError(null);
        setCopied(false);
        try {
            setReport(await managementGetActivityReport(axios, from, to));
        } catch (exception) {
            console.error(exception);
            setError(exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [axios, from, to, loading]);

    const copyAsText = useCallback(async () => {
        if (!report) return;
        try {
            await navigator.clipboard.writeText(activityReportAsText(report));
            setCopied(true);
        } catch (exception) {
            console.error(exception);
            setError('Could not copy to the clipboard.');
        }
    }, [report]);

    const downloadCsv = useCallback(() => {
        if (!report) return;
        const blob = new Blob([activityReportAsCsv(report)], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity-report-${report.from}-to-${report.to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }, [report]);

    const activeAccountRows = report
        ? report.accounts.filter(row =>
            row.created_this_month || row.services_created > 0 || row.signatures > 0 || row.services_started_or_stopped > 0)
        : [];

    return (
        <BaseModal
            title="Export Report"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            isFullScreen
            onFormSubmit={generate}
            error={error}
            actions={<>
                <button type="button" className="btn btn-sm btn-light" onClick={() => setIsOpen(false)}>
                    Close
                </button>
                <button type="button" className="btn btn-sm btn-light ml-2" disabled={!report} onClick={copyAsText}>
                    {copied ? 'Copied' : 'Copy as text'}
                </button>
                <button type="button" className="btn btn-sm btn-primary ml-2" disabled={!report} onClick={downloadCsv}>
                    Download CSV
                </button>
            </>}
        >
            <div className="flex flex-wrap items-end gap-4 mb-5">
                <label className="flex flex-col text-sm font-medium text-gray-900">
                    First month
                    <input
                        type="month"
                        name="from"
                        className="input input-sm mt-1"
                        required
                        pattern="\d{4}-(0[1-9]|1[0-2])"
                        placeholder="YYYY-MM"
                        max={thisMonth}
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                    />
                </label>
                <label className="flex flex-col text-sm font-medium text-gray-900">
                    Last month
                    <input
                        type="month"
                        name="to"
                        className="input input-sm mt-1"
                        required
                        pattern="\d{4}-(0[1-9]|1[0-2])"
                        placeholder="YYYY-MM"
                        min={from}
                        max={thisMonth}
                        value={to}
                        onChange={e => setTo(e.target.value)}
                    />
                </label>
                <button type="submit" className="btn btn-sm btn-primary" disabled={loading}>
                    {loading ? 'Counting...' : 'Generate'}
                </button>
                <div className="text-sm text-gray-500">
                    Calendar months in UTC. Demo accounts are left out. The current month counts to now.
                </div>
            </div>

            {loading && <LoadingSpinner />}

            {report && !loading && (
                <div className="flex flex-col gap-6">
                    <div className="text-sm text-gray-600">
                        {num(report.scope.accounts_counted)} accounts counted, {num(report.scope.demo_accounts_left_out)} demo accounts left out.
                        Generated {generatedLabel(report.generated_at)}.
                    </div>

                    <div className="overflow-x-auto">
                        <h4 className="text-md font-semibold mb-2">In each month</h4>
                        <table className="table table-border" data-testid="activity-report-summary">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 bg-gray-100 text-left">Month</th>
                                    {SUMMARY_COLUMNS.map(column => (
                                        <th key={column.key} className="px-4 py-2 bg-gray-100 text-right">{column.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {report.months.map(month => (
                                    <tr key={month.month} className="border-t">
                                        <td className="px-4 py-2">{monthLabel(month)}</td>
                                        {SUMMARY_COLUMNS.map(column => (
                                            <td key={column.key} className="px-4 py-2 text-right tabular-nums">{num(month[column.key] as number)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="overflow-x-auto">
                        <h4 className="text-md font-semibold mb-2">Totals at each month's end</h4>
                        <table className="table table-border" data-testid="activity-report-totals">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 bg-gray-100 text-left">Month end</th>
                                    {TOTAL_COLUMNS.map(column => (
                                        <th key={column.key} className="px-4 py-2 bg-gray-100 text-right">{column.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {report.months.map(month => (
                                    <tr key={month.month} className="border-t">
                                        <td className="px-4 py-2">{monthLabel(month)}</td>
                                        {TOTAL_COLUMNS.map(column => (
                                            <td key={column.key} className="px-4 py-2 text-right tabular-nums">{num(month[column.key] as number)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <details className="overflow-x-auto">
                        <summary className="cursor-pointer text-md font-semibold mb-2">
                            By account: {num(activeAccountRows.length)} rows with activity ({num(report.accounts.length)} in the CSV, one per account per month)
                        </summary>
                        <table className="table table-border" data-testid="activity-report-accounts">
                            <thead>
                                <tr>
                                    {ACCOUNT_COLUMNS.map(column => (
                                        <th key={column.key} className={`px-4 py-2 bg-gray-100 ${column.numeric ? 'text-right' : 'text-left'}`}>{column.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {activeAccountRows.map(row => (
                                    <tr key={`${row.month}-${row.account_unique_id}`} className="border-t">
                                        {ACCOUNT_COLUMNS.map(column => (
                                            <td key={column.key} className={`px-4 py-2 ${column.numeric ? 'text-right tabular-nums' : ''}`}>{cellText(row, column.key)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </details>

                    <details>
                        <summary className="cursor-pointer text-md font-semibold mb-2">What each number means</summary>
                        <ul className="list-disc pl-6 text-sm text-gray-700 flex flex-col gap-1">
                            {report.definitions.map(definition => <li key={definition}>{definition}</li>)}
                        </ul>
                    </details>
                </div>
            )}
        </BaseModal>
    );
}
